import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Dialog } from '@headlessui/react';
import {
  BookOpenIcon,
  Bars3Icon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { HomeIcon } from '@heroicons/react/20/solid';
import { DrawerModal } from '@fagmobler/ui';

import type { TNavigationItem } from '@/lib/sanity/queries';

import { useNavigation } from '@/contexts/navigation';
import { DrawerItem } from './DrawerItem';
import { getPathFromInternalLink } from '@/lib/utils/getUrls';
import clsx from 'clsx';

export function getIcon(title: string) {
  switch (title) {
    case 'produkter':
      return BookOpenIcon;

    case 'kampanjer':
      return ClockIcon;

    default:
      return HomeIcon;
  }
}

export function MainNavigation() {
  const {
    mainNavigation = [],
    setShowMainNavigation,
    showMainNavigation,
  } = useNavigation();
  const [selectedMainItem, setSelectedMainItem] =
    useState<TNavigationItem | null>(null);
  const [hasChildModal, setHasChildModal] = useState(!!selectedMainItem);

  const closeAll = () => {
    setHasChildModal(false);
    setSelectedMainItem(null);
    setShowMainNavigation(false);
  };

  let initialFocusRef = useRef<any>(null);

  const primaryNav = mainNavigation.filter(
    (item) => item.navType === 'primary'
  );
  const secondaryNav = mainNavigation.filter(
    (item) => item.navType !== 'tertiary' && item.navType !== 'primary'
  );
  const extraNav = mainNavigation.filter((item) => item.navType === undefined);

  return (
    <>
      <button
        className="bg-neutral-80 rounded-full p-3 flex items-center focus:outline-none focus-visible:ring-4 hover:underline underline-offset-4"
        title="Åpne meny"
        id="mainMenuButton"
        onClick={() => setShowMainNavigation(!showMainNavigation)}
      >
        <Bars3Icon className="h-5" />
        <span className="ml-2 hidden sm:block">Meny</span>
      </button>
      <div
        className={`${
          showMainNavigation ? 'lg:flex' : 'hidden lg:flex'
        } hidden lg:flex flex-nowrap`}
      >
        {primaryNav.map((item: TNavigationItem) => {
          // MAIN ITEMS (HEADER)
          if (!item.showInHeader) {
            return null;
          }

          const href =
            item.internalLink &&
            getPathFromInternalLink(item.internalLink, item);

          if (!item.children && href) {
            return (
              <Link
                href={href}
                key={`header_item_${item._key}`}
                className="flex items-center font-medium p-2 focus:outline-none focus:ring-4 ring-offset-focus hover:underline underline-offset-4"
              >
                {item.title}
              </Link>
            );
          }

          return (
            <button
              key={`disclosureButton_${item._key}`}
              className="font-medium p-2 focus:outline-none focus:ring-4 ring-offset-focus hover:underline underline-offset-4"
              onClick={() => {
                setSelectedMainItem(item);
                setShowMainNavigation(true);
              }}
              onKeyDown={(event: any) => {
                if (event.code === 'Escape') {
                  setShowMainNavigation(true);
                  setSelectedMainItem(item);
                }
              }}
            >
              {item.title}
            </button>
          );
        })}
      </div>

      <DrawerModal
        isOpen={showMainNavigation}
        onClose={() => {
          closeAll();
        }}
        position="left"
        unmount={false}
        className="max-w-sm"
      >
        <ul className="border-t border-neutral-40 p-6 text-neutral-80 pt-8">
          {primaryNav.map((item: TNavigationItem, i) => (
            <DrawerItem
              item={item}
              key={`primary_${item._key}`}
              mainItem
              closeDrawer={closeAll}
              onOpen={() => setHasChildModal(true)}
              onClose={() => {
                setSelectedMainItem(null);
                setHasChildModal(false);
              }}
              defaultOpen={selectedMainItem === item}
              ref={i === 0 ? initialFocusRef : undefined}
            />
          ))}
        </ul>
        <ul className={`border-t border-neutral-40 p-6 mt-4`}>
          {secondaryNav.map((item: TNavigationItem) => {
            const href =
              item.internalLink &&
              getPathFromInternalLink(item.internalLink, item);
            if (!href) return null;
            return (
              <li key={`secondaryItem_${item._key}`}>
                <Link
                  href={href}
                  className="block py-2 px-4 text-neutral-80 hover:underline underline-offset-4"
                  tabIndex={!!hasChildModal ? -1 : undefined}
                >
                  {item.title}
                </Link>
              </li>
            );
          })}
        </ul>
        <ul className={`pt-2 px-6 text-sm`}>
          {extraNav.map((item: TNavigationItem) => {
            const href =
              item.internalLink &&
              getPathFromInternalLink(item.internalLink, item);
            if (!href) return null;
            return (
              <li key={`extraNav_${item._key}`}>
                <Link
                  href={href}
                  className="block text-neutral-80 focus-visible:ring-2 ring-focus hover:underline underline-offset-4"
                  tabIndex={!!hasChildModal ? -1 : undefined}
                >
                  {item.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </DrawerModal>
    </>
  );
}

export default MainNavigation;
